import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as DodIcon } from '../../../../assets/icons/dot-circle.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { ReactComponent as ExpandIcon } from '../../../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { powerUnit } from '../../../../config/enum';
import {
  AvailableDevice,
  RequestDeviceInterFace,
} from '../../../../interface/device-management-partner.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import {
  fetchAllDevices,
  fetchListFilters,
  setDeviceFilterData,
  setSortData,
  updateDeviceFilters,
  refreshDevices,
  clearStationDevices,
} from '../../../../redux/slices/StationDashboardDeviceSlice';
import { setSelectedStation } from '../../../../redux/slices/StationManagementSidePanelSlice';
import { setSidePanelData } from '../../../../redux/slices/TableSidePanelSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../../shared-components/delete-warning/DeleteWarning';
import DisableSwitchWarning from '../../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../shared-components/switch-button-field/SwitchButton';
import ConnectorSidePanel from '../../../../shared-components/table-side-panel/ConnectorSidePanel';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import deviceApi from '../../../../utils/api/partner/device-partner';
import AddDevice from '../../../charger-pages/charger-management/components/AddDevice';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface DeviceManagementStationProperties {
  rightTopContent?: React.ReactNode;
}

/**
 * Device management station dashboard view component
 * @param {DeviceManagementStationProperties} properties - The component properties
 * @returns {any} The rendered component
 */
function DeviceManagementStation(properties: DeviceManagementStationProperties) {
  const { rightTopContent } = properties;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const { t } = useTranslation(['partnerDeviceManagement', 'sidePanel']);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { page: pageParameter } = useParams<{ page: string }>();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const {
    devices,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    sortBy,
    sortField,
    isFilterUpdated
  } = useAppSelector((states: RootState) => states.stationDashboardDevice);

  const displayedFilters = React.useMemo(() => {
    return selectedChargingPoint?.id
      ? filters.filter((f: any) => f.key !== 'chargingStation' && f.key !== 'stationId')
      : filters;
  }, [filters, selectedChargingPoint?.id]);


  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);

  /**
   * Handle the fetch data for Api call function
   * @param {RequestDeviceInterFace} data - RequestDeviceInterFace
   */
  const fetchData = (data: RequestDeviceInterFace) => {
    dispatch(
      fetchAllDevices({
        ...data,
        ...(selectedChargingPoint?.id ? { stationId: [selectedChargingPoint.id] } : {}),
      }),
    );
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(displayedFilters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Handles the pagination
   * @param {any} value - The page or limit details
   */
  const handlePageChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(displayedFilters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      ...value,
    });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(displayedFilters);
    dispatch(
      setDeviceFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Updates the availability status of a device in the local state
   * @param {string} deviceId - The ID of the device to update
   * @param {boolean} isAvailable - The new availability status
   */
  const updateAvailability = (deviceId: string, isAvailable: boolean) => {
    deviceApi
      .editAvailableStatusDevice(deviceId, { availabilityStatus: isAvailable })
      .then(() => {
        fetchData({
          page,
          limit,
        });
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('statusChangedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableDevice, checked: boolean) => {
    if (checked) {
      updateAvailability(device._id, checked);
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        header: false,
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableDeviceWarning')}
            onConfirm={() => updateAvailability(device._id, checked)}
          />
        ),
        notificationModal: true,
      });
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   * @returns {any} The rendered switch component
   */
  const renderAvailability = (device: AvailableDevice) => (
    <div
      role="presentation"
      className="flex justify-center"
      onClick={(even) => even.stopPropagation()}>
      <div key={device.chargingStation}>
        <SwitchButton
          checked={device.isAvailable}
          disabled={
            (device.isAvailable &&
              !permissionCheck('partnerChargingDevicesDeactivate') &&
              !permissionCheck('cpoChargingPointStatusUpdate')) ||
            (!device.isAvailable &&
              !permissionCheck('partnerChargingDevicesActivate') &&
              !permissionCheck('cpoChargingPointStatusUpdate'))
          }
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * UI of Status
   * @param {AvailableDevice} device - The device data
   * @returns {any} The rendered status dot/tag
   */
  const renderStatus = (device: AvailableDevice) =>
    device.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DodIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );

  /**
   * Function to open device view model
   * @param {AvailableDevice} chargingStationDate - The chargingStationDate
   * @param {boolean} newTab - Whether to open in new tab
   */
  const handleOpenModelView = (chargingStationDate: AvailableDevice, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id: chargingStationDate?.chargingStationId,
          name: chargingStationDate?.chargingStation,
        }),
      );

      const basePath = location.pathname.includes('charger-expanded')
        ? 'station-expanded'
        : 'station-management';
      const sourcePath =
        location.state?.source ||
        new URLSearchParams(location.search).get('source') ||
        location.pathname + location.search;

      if (newTab) {
        const url = `/ev-admin/${basePath}/${chargingStationDate.chargingStationId}/station-dashboard?searchBy=${chargingStationDate.chargingStation}&source=${sourcePath}`;
        window.open(url, '_blank');
      } else {
        navigate(
          {
            pathname: `/ev-admin/${basePath}/${chargingStationDate.chargingStationId}/station-dashboard`,
            search: `?searchBy=${chargingStationDate.chargingStation}`,
          },
          { state: { source: sourcePath } },
        );
      }
    } else {
      console.error('Error while opening device in separate page');
    }
  };

  /**
   * Triggers the device side panel in edit mode.
   * @param {string} deviceId - The ID of the device.
   */
  const handleEditDeviceClick = (deviceId: string) => {
    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = ['stationId', 'connectorDeviceId', 'facilityId'];
    sidePanelKeys.forEach((key) => parameters.delete(key));

    parameters.set('deviceId', deviceId);
    parameters.set('action', 'edit');
    setIsEditMode(true);
    setSearchParameters(parameters);
  };

  /**
   * Handle changes to edit mode.
   * @param {boolean} isEdit - True to enable edit mode, false to disable.
   */
  const handleEditModeChange = (isEdit: boolean) => {
    setIsEditMode(isEdit);
    const newParameters = new URLSearchParams(searchParameters);
    if (isEdit) {
      newParameters.set('action', 'edit');
    } else {
      newParameters.delete('action');
    }
    setSearchParameters(newParameters);
  };

  /**
   * Calls the API to delete a device.
   * @param {string} deviceId - The ID of the device to delete.
   */
  const handleConfirmDeleteDevice = (deviceId: string) => {
    deviceApi
      .deleteDevice(deviceId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('deviceDeletedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({ page, limit });
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Triggers the delete confirmation warning modal.
   * @param {string} deviceId - The ID of the device.
   */
  const handleDeleteDeviceClick = (deviceId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={deviceId}
          title={t('deleteConfirmTitle')}
          description={t('deleteConfirmDescription')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id: string | any) => id && handleConfirmDeleteDevice(id)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Function to expand the device
   * @param {string} deviceId - The ID of the device to expand
   */
  const handleOpenDeviceInNewTab = (deviceId: string | any) => {
    const deviceDetails = devices.find(
      (value: any) => value.id === deviceId || value._id === deviceId,
    );
    dispatch(
      setSelectedDevice({
        id: deviceDetails?.id,
        name: deviceDetails?.chargingPointName,
        chargingPointSn: deviceDetails?.chargingPointSn,
      }),
    );
    const sourcePath =
      location.state?.source ||
      new URLSearchParams(location.search).get('source') ||
      location.pathname + location.search;
    const url = `/ev-admin/charger-expanded/${deviceDetails?.id}/device-details?searchBy=${deviceDetails?.chargingPointName}&source=${sourcePath}`;
    window.open(url, '_blank');
  };

  /**
   * Function to view the device
   * @param {string} deviceId - The ID of the device to view
   */
  const handleExpandDevice = (deviceId: string | any) => {
    const deviceDetails = devices.find(
      (value: any) => value.id === deviceId || value._id === deviceId,
    );
    dispatch(
      setSelectedDevice({
        id: deviceDetails?.id,
        name: deviceDetails?.chargingPointName,
        chargingPointSn: deviceDetails?.chargingPointSn,
      }),
    );
    const sourcePath =
      location.state?.source ||
      new URLSearchParams(location.search).get('source') ||
      location.pathname + location.search;
    navigate(
      {
        pathname: `/ev-admin/charger-management/${deviceDetails?.id}/device-details`,
        search: `?searchBy=${deviceDetails?.chargingPointName}&source=${sourcePath}`,
      },
      { state: { source: sourcePath } },
    );
  };

  /**
   * Returns side panel header content: title, expand icon, edit, and delete actions.
   * @returns {any} Header title and actions element
   */
  const renderSidePanelTopLeftContent = () => {
    if (sidePanelOpen) {
      return <div className="side-panel-header-title">{t('addDevice')}</div>;
    }

    const stationIdFromParameter = searchParameters.get('stationId');
    if (stationIdFromParameter) {
      const device = devices.find(
        (d: AvailableDevice) => d.chargingStationId === stationIdFromParameter,
      );
      const deviceObject = device || {
        chargingStationId: stationIdFromParameter,
        chargingStation: '',
      };
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isEditMode
              ? t('sidePanel:editStationDetails', 'Edit Station Details')
              : t('sidePanel:stationDetails', 'Station Details')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() => handleOpenModelView(deviceObject as AvailableDevice, false)}>
                    <ExpandIcon className="icon" />
                  </span>
                  <div className="hover-label">{t('sidePanel:expandPages', 'Expand Pages')}</div>
                </div>
                {(permissionCheck('partnerChargingStationUpdate') ||
                  permissionCheck('cpoChargingStationUpdate')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={() => handleEditModeChange(true)}>
                        <EditIcon className="icon" />
                      </span>
                      <div className="hover-label">{t('edit', 'Edit')}</div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      );
    }

    const connectorDeviceId = searchParameters.get('connectorDeviceId');
    if (connectorDeviceId) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('sidePanel:connectorDetails', 'Connector Details')}
          </p>
        </div>
      );
    }

    const deviceId = searchParameters.get('deviceId');
    if (!deviceId) return null;

    return (
      <div className="side-panel-header-left-title-actions">
        <p className="side-panel-header-title">
          {isEditMode ? t('editDevice', 'Edit Device') : t('deviceDetails', 'Device Details')}
        </p>
        <div className="side-panel-header-left-icons">
          {!isEditMode && (
            <>
              <div className="button-wrap">
                <span
                  role="presentation"
                  className="access-facility"
                  onClick={() => handleExpandDevice(deviceId)}>
                  <ExpandIcon />
                </span>
                <div className="hover-label">{t('expandPages', 'Expand Pages')}</div>
              </div>

              {(permissionCheck('partnerChargingDevicesUpdate') ||
                permissionCheck('cpoChargingPointUpdate')) && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleEditModeChange(true)}>
                      <EditIcon className="icon" />
                    </span>
                    <div className="hover-label">{t('edit', 'Edit')}</div>
                  </div>
                )}

              {(permissionCheck('partnerChargingDevicesDelete') ||
                permissionCheck('cpoChargingPointDelete')) && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleDeleteDeviceClick(deviceId)}>
                      <DeleteIcon className="icon delete" />
                    </span>
                    <div className="hover-label">{t('delete', 'Delete')}</div>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    );
  };

  const column = [
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof AvailableDevice,
      queryParamsKey: 'deviceId',
      sidePanelComponent: (
        <DeviceSidePanel
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          fetchIdFrom="queryParams"
          hideHeader
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      sortAllow: true,
    },
    {
      label: t('chargerBrand'),
      accessorKey: 'chargePointVendor' as keyof AvailableDevice,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('chargerModel'),
      accessorKey: 'chargePointModel' as keyof AvailableDevice,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof AvailableDevice,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn' as keyof AvailableDevice,
    },
    {
      label: t('connectors'),
      accessorKey: 'connectorsStatus' as keyof AvailableDevice,
      queryParamsKey: 'connectorDeviceId',
      sidePanelComponent: <ConnectorSidePanel fetchIdFrom="queryParams" showHeader={false} />,
      /**
       * Renders the total number of connectors available for the device.
       * @param {AvailableDevice} device - The device whose connector count is displayed.
       * @returns {any} A paragraph element containing the connector count.
       */
      customData: (device: AvailableDevice): React.JSX.Element => (
        <p
          className="table-row-each-data hyperlink"
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
            const parameters = new URLSearchParams(searchParameters);
            const sidePanelKeys = [
              'deviceId',
              'stationId',
              'facilityId',
              'action',
            ];
            sidePanelKeys.forEach((key) => parameters.delete(key));
            parameters.set('connectorDeviceId', device._id);
            setSearchParameters(parameters);
            dispatch(
              setSidePanelData({
                key: 'DeviceManagementStation',
                value: device._id,
              }),
            );
          }}>
          {Object.keys(device.connectorsStatus || {}).length || device.connectors?.length || 0}{' '}
          <span>
            Connector
            {(Object.keys(device.connectorsStatus || {}).length ||
              device.connectors?.length ||
              0) !== 1 && 's'}
          </span>
        </p>
      ),
    },
    {
      label: t('tariff'),
      accessorKey: 'tariff' as keyof AvailableDevice,
      /**
       * Renders the tariff attachment status for the device.
       * @param {AvailableDevice} device - The device whose tariff status is checked.
       * @returns {React.JSX.Element} A paragraph element containing the tariff status.
       */
      customData: (device: AvailableDevice): React.JSX.Element => {
        const connectors = device.connectors || [];

        const tariffCount = connectors.filter(
          (connector) => connector.tariffId || connector.tariffName,
        ).length;
        const notAppliedCount = connectors.length - tariffCount;

        const hasTariff = connectors.length > 0 && tariffCount === connectors.length;
        const isMixed = tariffCount > 0 && notAppliedCount > 0;

        let tariffClassName = '';
        if (!isMixed) {
          tariffClassName = hasTariff ? 'tariff-attached' : 'tariff-not-attached';
        }

        return (
          <p className={tariffClassName}>
            {isMixed ? (
              <>
                <span className="tariff-attached">
                  {tariffCount} {t('applied', 'Applied')}
                </span>{' '}
                and{' '}
                <span className="tariff-not-attached">
                  {notAppliedCount} {t('notApplied', 'Not Applied')}
                </span>
              </>
            ) : (
              <>
                <span>{hasTariff ? tariffCount : notAppliedCount}</span>{' '}
                {hasTariff ? t('applied', 'Applied') : t('notApplied', 'Not Applied')}
              </>
            )}
          </p>
        );
      },
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof AvailableDevice,
      /**
       * Renders the connector types available for the device.
       * @param {AvailableDevice} device - The device whose connector types are displayed.
       * @returns {any} A paragraph element containing the connector types.
       */
      customData: (device: AvailableDevice): React.JSX.Element => (
        <p>{device?.connectors?.map((connector) => connector.connectorType).join(', ')}</p>
      ),
    },
    {
      label: t('currentType'),
      accessorKey: 'currentType' as keyof AvailableDevice,
      /**
       * Renders the current types available for the device.
       * @param {AvailableDevice} device - The device whose current types are displayed.
       * @returns {any} A paragraph element containing the current types.
       */
      customData: (device: AvailableDevice): React.JSX.Element => <p>{device?.currentType}</p>,
    },
    {
      label: t('maxPowerOutput'),
      accessorKey: 'maximumOutputPower' as keyof AvailableDevice,
      sortAllow: true,
      /**
       * Data UI in table
       * @param {AvailableDevice} device - The device data
       * @returns {any} The output element
       */
      customData: (device: AvailableDevice) => (
        <div>
          {device?.maximumOutputPower && (
            <span>{`${ToFixedValue(device?.maximumOutputPower, 3)} ${device?.maximumOutputPowerUnit === 'A' ? powerUnit.A : powerUnit.KWH}`}</span>
          )}
        </div>
      ),
    },
    {
      label: 'Availability Status',
      accessorKey: 'isAvailable' as keyof AvailableDevice,
      customData: renderAvailability,
      hideWhenSidePanelOpen: true,
    },
    {
      label: 'Connection Status',
      accessorKey: 'isDeviceOffline' as keyof AvailableDevice,
      customData: renderStatus,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('actions'),
      accessorKey: 'actions' as keyof AvailableDevice,
      columnWidth: 180,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Data UI in table
       * @param {AvailableDevice} device - The device data
       * @returns {any} Action buttons element
       */
      customData: (device: AvailableDevice) => (
        <div className="action-buttons-wrap">
          {device?.chargingPointSn && (
            <>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleExpandDevice(device._id);
                }}>
                <ExpandIcon className="icon expand" />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenDeviceInNewTab(device._id);
                }}>
                <NewTabIcon className="icon new-tab" />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    !permissionCheck('partnerChargingDevicesUpdate') &&
                    !permissionCheck('cpoChargingPointUpdate')
                  ) {
                    return;
                  }
                  handleEditDeviceClick(device._id);
                }}>
                <EditIcon
                  className={`icon edit ${!permissionCheck('partnerChargingDevicesUpdate') && !permissionCheck('cpoChargingPointUpdate') ? 'disabled-icon' : ''}`}
                />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    !permissionCheck('partnerChargingDevicesDelete') &&
                    !permissionCheck('cpoChargingPointDelete')
                  ) {
                    return;
                  }
                  handleDeleteDeviceClick(device._id);
                }}>
                <DeleteIcon
                  className={`icon delete ${!permissionCheck('partnerChargingDevicesDelete') && !permissionCheck('cpoChargingPointDelete') ? 'disabled-icon' : ''}`}
                />
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  const currentColumns = [
    { label: t('sNo', 'S.No'), accessorKey: '__serial__' },
    { label: t('deviceName', 'Charger Name'), accessorKey: 'chargingPointName' },
    { label: t('chargerBrand', 'Charger Brand'), accessorKey: 'chargePointVendor' },
    { label: t('deviceModel', 'Charger Model'), accessorKey: 'chargePointModel' },
    { label: t('stationName', 'Station Name'), accessorKey: 'chargingStation' },
    { label: t('chargerSN', 'Charger SN'), accessorKey: 'chargingPointSn' },
    { label: t('connectors', 'Connector(s)'), accessorKey: 'connectors' },
    { label: t('connectorType', 'Connector Type'), accessorKey: 'connectorType' },
    { label: t('currentType', 'Current Type'), accessorKey: 'currentType' },
    { label: t('maxPowerOutput', 'Max Power Output'), accessorKey: 'maximumOutputPower' },
    { label: t('availability', 'Availability'), accessorKey: 'isAvailable' },
    { label: t('deviceStatus', 'Device Status'), accessorKey: 'isDeviceOffline' },
    { label: t('Created At'), accessorKey: 'createdAt' },
    { label: t('Updated At'), accessorKey: 'updateAt' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - The format type (csv or pdf)
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(displayedFilters);
    deviceApi
      .fetchDevicesExport(
        removeEmptyKeys({
          ...queryParameters,
          sortBy,
          sortField,
          ...(selectedChargingPoint?.id ? { stationId: [selectedChargingPoint.id] } : {}),
        }),
      )
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableDevice, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'maximumOutputPower':
                return `${ToFixedValue(row?.maximumOutputPower, 3)} ${row?.maximumOutputPowerUnit === 'A' ? powerUnit.A : powerUnit.KWH}`;
              case 'connectorType':
                return `${row?.connectors?.map((c) => c.connectorType).join(', ') || 'N/A'}`;
              case 'connectors': {
                const count =
                  Object.keys(row?.connectorsStatus || {}).length || row?.connectors?.length || 0;
                return `${count} Connector${count !== 1 ? 's' : ''}`;
              }
              case 'isAvailable':
                return `${row?.isAvailable ? t('available', 'Available') : t('unavailable', 'Unavailable')}`;
              case 'isDeviceOffline':
                return `${row?.isDeviceOffline ? t('offline', 'Offline') : t('online', 'Online')}`;
              case 'createdAt':
                return ConvertUTCtoDateTime(row?.createdAt || '');
              case 'updateAt':
                return ConvertUTCtoDateTime(row?.updatedAt || '');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'devices-list.csv');
          } else {
            exportPDF(headers, rows, 'devices-list.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function for handling auto refresh
   * Each 10 sec triggers auto refresh from header component
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(displayedFilters);

    dispatch(
      refreshDevices({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        ...(selectedChargingPoint?.id ? { stationId: [selectedChargingPoint.id] } : {}),
      }),
    );
  };

  /**
   * Handle the open side panel on row click function
   * @param {string} _ - The row id
   * @param {AvailableDevice} row - The device data
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableDevice) => {
    if (!row?._id) return;

    const parameters = new URLSearchParams(searchParameters);
    const sidePanelKeys = [
      'stationId',
      'connectorDeviceId',
      'facilityId',
      'action',
    ];
    sidePanelKeys.forEach((key) => parameters.delete(key));

    parameters.set('deviceId', row._id);
    setSearchParameters(parameters);
  };

  useEffect(() => {
    if (selectedChargingPoint?.id) {
      dispatch(clearStationDevices());
    }
    return () => {
      dispatch(clearStationDevices());
    };
  }, [dispatch, selectedChargingPoint?.id]);

  useEffect(() => {
    if (isFilterUpdated || devices.length === 0 || selectedChargingPoint?.id) {
      const queryParameters = GetParametersFromCommonFilters(displayedFilters);

      fetchData({
        ...queryParameters,
        page,
        limit,
      });
    }
  }, [filters, selectedChargingPoint?.id]);

  useEffect(() => {
    if (
      searchParameters.get('deviceId') ||
      searchParameters.get('stationId') ||
      searchParameters.get('connectorDeviceId') ||
      searchParameters.get('facilityId') ||
      searchParameters.get('action')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('deviceId');
      parameters.delete('stationId');
      parameters.delete('connectorDeviceId');
      parameters.delete('facilityId');
      parameters.delete('action');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (
      !searchParameters.get('deviceId') &&
      !searchParameters.get('stationId') &&
      !searchParameters.get('connectorDeviceId') &&
      !searchParameters.get('facilityId')
    ) {
      setIsEditMode(false);
      if (searchParameters.get('action')) {
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
      }
    }
  }, [searchParameters]);

  useEffect(() => {
    if (pageParameter === 'add') {
      setSidePanelOpen(true);
    }
  }, [pageParameter]);

  useEffect(() => {
    const status = searchParameters.get('connectorStatus');

    if (status) {
      dispatch(
        updateDeviceFilters({
          key: 'connectorStatus',
          value: status,
          type: 'restAndAdd',
        }),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    const filterWithOutSearchBy = filters.filter((eachFilter) => eachFilter.key !== 'searchBy');
    if (filterWithOutSearchBy.length === 0 && !filterListLoading) {
      dispatch(fetchListFilters());
    }
  }, [dispatch, filters, filterListLoading, selectedChargingPoint?.id, displayedFilters]);

  return (
    <div className="device-management-wrap-partner">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="device-table-container">
        <CustomTable
          dataKey="DeviceManagementStation"
          loading={loading}
          limit={limit}
          header={column}
          version="v2"
          data={devices}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          page={page}
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          handleSortChange={handleSortFunction}
          pageLimitChange={handlePageChange}
          exportDataPdfOrCSV={
            permissionCheck('partnerChargingDevicesExport') ||
              permissionCheck('cpoChargingPointExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          exportLoader={exportLoader}
          filters={displayedFilters}
          filtersToDispatch={updateDeviceFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          hoverLabel={t('refresh')}
          serialNo
          created
          lastUpdated
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="deviceId"
          rowClickSidePanel={
            <DeviceSidePanel
              isEditMode={isEditMode}
              onEditModeChange={handleEditModeChange}
              fetchIdFrom="queryParams"
              hideHeader
              onSuccess={() => {
                handleEditModeChange(false);
                handleRefresh();
              }}
            />
          }
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={<AddDevice setSidePanelOpen={setSidePanelOpen} hideHeader />}
          onSidePanelClose={() => {
            const parameters = new URLSearchParams(searchParameters);
            parameters.delete('facilityId');
            parameters.delete('deviceId');
            parameters.delete('connectorDeviceId');
            parameters.delete('stationId');
            parameters.delete('action');

            setSearchParameters(parameters);

            setSidePanelOpen(false);
            setIsEditMode(false);
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          rightTopContent={
            <div className="table-bottom-button-list">
              {rightTopContent && <div className="table-button-item">{rightTopContent}</div>}
              <div className="table-button-item">
                <MyButton
                  label={t('addDevice')}
                  buttonType="submit"
                  onClickFunc={() => setSidePanelOpen(true)}
                  svgIcon={<PlusIcon />}
                  disableButton={
                    !permissionCheck('partnerChargingDevicesAdd') &&
                    !permissionCheck('cpoChargingPointAdd')
                  }
                />
              </div>
            </div>
          }
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
        />
      </div>
    </div>
  );
}

export default DeviceManagementStation;
